import {
  offset,
  useDismiss,
  useFloating,
  useInteractions
} from '@floating-ui/react'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  faCalendarDays,
  faClipboardCheck,
  faEllipsisVertical,
  faEnvelope,
  faLink,
  faListCheck,
  faMessage,
  faStickyNote
} from '@fortawesome/pro-solid-svg-icons'
import { usePostHog } from 'posthog-js/react'
import type { ReactNode } from 'react'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { ActionsMenu } from '~/components/Global/Interactions/ActionsMenu/ActionsMenu'
import { ActionsMenuItem } from '~/components/Global/Interactions/ActionsMenu/ActionsMenuItem'
import { InlineOnboarding } from '~/components/Global/Onboarding/InlineOnboarding'
import { confirmCloseAction } from '~/components/Scopes/Actions/ActionUtils'
import { AddToOrderAction } from '~/components/Scopes/Actions/AddToOrderAction'
import { EmailAction } from '~/components/Scopes/Actions/EmailAction'
import { getAsideActionGlobalRef } from '~/components/Scopes/Actions/Global/asideActionRef'
import { MeetingAction } from '~/components/Scopes/Actions/MeetingAction'
import { MessageAction } from '~/components/Scopes/Actions/MessageAction'
import { NoteAction } from '~/components/Scopes/Actions/NoteAction'
import { ActionTrigger } from '~/domains/actions/components/ActionTrigger/ActionTrigger'
import {
  type ApplicantAction,
  captureApplicantAction
} from '~/domains/applicants/analytics'
import { useConfig } from '~/domains/configuration/queries/config'
import { useEtestingLaunch } from '~/domains/extensions/etesting/hooks/useEtestingLaunch'
import { TaskAction } from '~/domains/tasks/components/TaskAction/TaskAction'
import type { AsideActionPosition } from '~/types/global'
import { AsideActionContext, type AsideActionOptions } from '~/utils/contextes'
import {
  actions,
  emailChoiceContainer,
  emailChoiceOption,
  emailChoiceOptionDisabled
} from './ActionsButtonList.css'
import {
  type ActionTargetType,
  actionsForTarget,
  type TargetActionName
} from './actionsForTarget'

const actionNameMap = {
  call: 'Ajouter une tâche',
  message: 'Envoyer un SMS',
  email: 'Envoyer un email',
  meeting: 'Planifier un rendez-vous',
  addToOrder: 'Associer à une commande',
  note: 'Ajouter une note'
}

export type ActionsButtonListTarget = {
  type: ActionTargetType
  ids: number[]
}

export const ActionsButtonList = ({
  target,
  contextOrder,
  removeTargetIdFromList,
  onActionHover,
  onCurrentActionChange,
  availablePositions,
  defaultPosition,
  isLegacy = false,
  hasEmail = true,
  hiddenActions = [],
  disableMoreActionsMenu = false,
  showTaskOnboarding = false
}: {
  target: ActionsButtonListTarget
  contextOrder?: {
    id: number
    willChangeStatus: boolean
  }
  removeTargetIdFromList?: (id: number) => void
  onActionHover?: (action: string | null) => void
  onCurrentActionChange?: (action: string | null) => void
  availablePositions?: AsideActionPosition[]
  defaultPosition?: AsideActionPosition
  isLegacy?: boolean
  hasEmail?: boolean
  hiddenActions?: TargetActionName[]
  /** Renders every action as a plain ActionTrigger instead of grouping extras behind a "more actions" menu — e.g. the legacy applicant sheet navbar already has its own overflow menu */
  disableMoreActionsMenu?: boolean
  /** Points out that the call action became a task action; only where the list is rendered once (not in tables or kanbans) */
  showTaskOnboarding?: boolean
}) => {
  const posthog = usePostHog()
  const {
    setAsideAction: contextSetAsideAction,
    isDirty,
    resetAction,
    asideAction
  } = useContext(AsideActionContext)
  const isContact = target.type === 'contact'
  const targetIds = target.ids
  const targetProps = isContact
    ? { contactIds: targetIds }
    : { applicantIds: targetIds }
  const visibleActions = actionsForTarget(target.type, hiddenActions)
  const missingEmailReason = isContact
    ? "Le contact n'a pas d'adresse email renseignée"
    : "Le candidat n'a pas d'adresse email renseignée"
  const { data: recallLimit } = useConfig(
    isContact
      ? 'actions_limit.contact_recall'
      : 'actions_limit.applicant_recall'
  )
  const { data: meetingLimit } = useConfig(
    isContact
      ? 'actions_limit.contact_meeting'
      : 'actions_limit.applicant_meeting'
  )
  const { data: smsLimit } = useConfig('actions_limit.sms')
  const { data: applicantLinkOrderLimit } = useConfig(
    'actions_limit.applicant_link_order'
  )
  const { data: emailLimit } = useConfig('actions_limit.email')
  const [emailChoice, setEmailChoice] = useState(false)
  const taskTriggerRef = useRef<HTMLDivElement>(null)
  const {
    isAvailable: isEtestingAvailable,
    isPending: isEtestingPending,
    launch: launchEtesting
  } = useEtestingLaunch()

  // Retain the original action list to active only self buttons when the action is triggered
  // In case like kanban with ton of action list, it avoid to have all action list buttons active
  const [originalActionList, setOriginalActionList] = useState(false)
  useEffect(() => {
    if (asideAction === null && originalActionList) {
      setOriginalActionList(false)
    }
  }, [originalActionList, asideAction])

  // Memo the current action based on context to highlight the correct button
  const currentAction = useMemo((): keyof typeof actionNameMap | null => {
    if (
      asideAction &&
      typeof asideAction === 'object' &&
      'type' in asideAction
    ) {
      switch (asideAction.type) {
        case TaskAction:
          return 'call'
        case MessageAction:
          return 'message'
        case EmailAction:
          return 'email'
        case MeetingAction:
          return 'meeting'
        case AddToOrderAction:
          return 'addToOrder'
        case NoteAction:
          return 'note'
      }
    }
    return null
  }, [asideAction])

  const setAsideAction = useCallback(
    ({ action, isWide, position, availablePositions }: AsideActionOptions) => {
      if (isLegacy) {
        getAsideActionGlobalRef().current(action, {
          isWide,
          position,
          availablePositions
        })
      } else {
        contextSetAsideAction({
          action,
          isWide,
          position,
          availablePositions
        })
      }
    },
    [contextSetAsideAction, isLegacy]
  )

  const setAction = useCallback(
    (action: ReactNode, isWide?: boolean, position?: AsideActionPosition) => {
      setAsideAction({
        action,
        position:
          position && availablePositions?.includes(position)
            ? position
            : defaultPosition,
        availablePositions: availablePositions,
        isWide
      })
    },
    [setAsideAction, defaultPosition, availablePositions]
  )

  const closeAsideAction = async () => {
    if (!isDirty) {
      resetAction()
      return true
    }
    const confirm = await confirmCloseAction(true)
    if (confirm) {
      resetAction()
      return true
    }
    return false
  }

  const handleClick =
    (callback: () => void) => async (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      const confirm = await closeAsideAction()
      if (confirm) {
        setOriginalActionList(true)
        callback()
      }
    }

  useEffect(() => {
    onCurrentActionChange?.(currentAction ? actionNameMap[currentAction] : null)
  }, [currentAction, onCurrentActionChange])

  const {
    refs: emailChoiceRefs,
    floatingStyles: emailChoiceStyles,
    context: contextEmailChoice
  } = useFloating({
    open: emailChoice,
    onOpenChange: setEmailChoice,
    placement: 'bottom-end',
    middleware: [offset(10)]
  })

  const adaptablePosition = availablePositions?.includes('modal')
    ? 'modal'
    : availablePositions?.includes('sidebar')
      ? 'sidebar'
      : 'bottom'

  const dismiss = useDismiss(contextEmailChoice)
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])
  const origin =
    defaultPosition === 'bottom'
      ? 'applicant_modal'
      : contextOrder?.id
        ? 'order_kanban'
        : isLegacy
          ? 'legacy'
          : 'table_or_list'

  const trackAction = (action: ApplicantAction) => {
    if (isContact) {
      return
    }
    captureApplicantAction(posthog, action, {
      origin,
      applicantCount: targetIds.length,
      hasOrderContext: Boolean(contextOrder?.id),
      orderId: contextOrder?.id
    })
  }

  const extraActions: {
    key: string
    icon: IconProp
    title: string
    onClick: (e: React.MouseEvent<HTMLElement>) => void
    loading?: boolean
    active?: boolean
    disabledReason?: string
  }[] = []
  if (visibleActions.includes('note')) {
    extraActions.push({
      key: 'note',
      icon: faStickyNote,
      title: actionNameMap.note,
      active: currentAction === 'note' && originalActionList,
      onClick: handleClick(() => {
        trackAction('note')
        setAction(
          <NoteAction {...targetProps} orderContext={contextOrder} />,
          false,
          adaptablePosition
        )
      })
    })
  }
  if (visibleActions.includes('etesting') && isEtestingAvailable) {
    extraActions.push({
      key: 'etesting',
      icon: faClipboardCheck,
      title: 'Envoyer un test',
      loading: isEtestingPending,
      disabledReason: hasEmail ? undefined : missingEmailReason,
      onClick: (e) => {
        e.stopPropagation()
        if (isEtestingPending || !hasEmail) {
          return
        }
        void launchEtesting(targetIds)
      }
    })
  }
  const groupExtraActions = !disableMoreActionsMenu && extraActions.length > 1

  return (
    <div className={actions}>
      {visibleActions.includes('call') && (
        <ActionTrigger
          ref={taskTriggerRef}
          isLegacy={isLegacy}
          icon={faListCheck}
          title={actionNameMap.call}
          onClick={handleClick(() => {
            trackAction('call')
            setAction(
              <TaskAction {...targetProps} orderContext={contextOrder} />,
              false,
              adaptablePosition
            )
          })}
          active={currentAction === 'call' && originalActionList}
          onActionHover={(hover) =>
            onActionHover?.(hover ? actionNameMap.call : null)
          }
          limitReached={
            recallLimit !== undefined && targetIds.length > recallLimit
              ? {
                  limit: recallLimit,
                  label: 'appel'
                }
              : false
          }
        />
      )}
      <InlineOnboarding
        title="Ajouter une tâche"
        details="L'ajout d'appel devient l'ajout de tâche. L'appel reste proposé par défaut : changez le type pour créer une tâche libre avec son propre sujet, ou une tâche personnalisée de votre agence."
        anchorRef={taskTriggerRef}
        configName="onboarding.task_action_onboarding"
        show={showTaskOnboarding}
      />
      {visibleActions.includes('message') && (
        <ActionTrigger
          isLegacy={isLegacy}
          icon={faMessage}
          title={actionNameMap.message}
          onClick={handleClick(() => {
            trackAction('sms')
            setAction(
              <MessageAction
                {...targetProps}
                removeRecipientIdFromList={removeTargetIdFromList}
              />,
              false,
              adaptablePosition
            )
          })}
          active={currentAction === 'message' && originalActionList}
          onActionHover={(hover) =>
            onActionHover?.(hover ? actionNameMap.message : null)
          }
          limitReached={
            smsLimit && targetIds.length > smsLimit
              ? {
                  limit: smsLimit,
                  label: 'appel'
                }
              : false
          }
        />
      )}
      {visibleActions.includes('email') && (
        <>
          <ActionTrigger
            isLegacy={isLegacy}
            icon={faEnvelope}
            title={actionNameMap.email}
            onClick={(e) => {
              e.stopPropagation()
              if (isContact) {
                handleClick(() => {
                  setAction(
                    <EmailAction contactsIds={targetIds} sendTo="contact" />,
                    true,
                    adaptablePosition
                  )
                })(e)
              } else if (contextOrder?.id) {
                trackAction('email_menu_open')
                setEmailChoice(!emailChoice)
              } else {
                handleClick(() => {
                  trackAction('email_to_applicant')
                  setAction(
                    <EmailAction
                      applicantIds={targetIds}
                      sendTo="applicant"
                      orderContext={contextOrder}
                      removeApplicantIdFromList={removeTargetIdFromList}
                    />,
                    true,
                    adaptablePosition
                  )
                })(e)
              }
            }}
            active={
              (currentAction === 'email' && originalActionList) || emailChoice
            }
            ref={emailChoiceRefs.setReference}
            onActionHover={(hover) =>
              onActionHover?.(hover ? actionNameMap.email : null)
            }
            limitReached={
              emailLimit !== undefined && targetIds.length > emailLimit
                ? {
                    limit: emailLimit,
                    label: 'email'
                  }
                : false
            }
            disabledReason={
              !hasEmail && !contextOrder?.id ? missingEmailReason : undefined
            }
            {...getReferenceProps()}
          />
          {emailChoice &&
            createPortal(
              <div
                className={emailChoiceContainer}
                ref={emailChoiceRefs.setFloating}
                style={emailChoiceStyles}
                {...getFloatingProps()}
              >
                <div
                  className={
                    hasEmail ? emailChoiceOption : emailChoiceOptionDisabled
                  }
                  onClick={
                    hasEmail
                      ? handleClick(() => {
                          setEmailChoice(false)
                          trackAction('email_to_applicant')
                          setAction(
                            <EmailAction
                              applicantIds={targetIds}
                              sendTo="applicant"
                              orderContext={contextOrder}
                              removeApplicantIdFromList={removeTargetIdFromList}
                            />,
                            true,
                            adaptablePosition
                          )
                        })
                      : (e) => e.stopPropagation()
                  }
                  title={!hasEmail ? missingEmailReason : undefined}
                >
                  Envoyer un email{' '}
                  {targetIds.length === 1
                    ? 'au candidat'
                    : 'aux candidats'}{' '}
                </div>
                <div
                  className={emailChoiceOption}
                  onClick={handleClick(() => {
                    setEmailChoice(false)
                    trackAction('email_to_contact')
                    setAction(
                      <EmailAction
                        applicantIds={targetIds}
                        sendTo="contact"
                        orderContext={contextOrder}
                      />,
                      true,
                      adaptablePosition
                    )
                  })}
                >
                  Envoyer par email au contact
                </div>
              </div>,
              document.querySelector('#appWrapper') as HTMLElement
            )}
        </>
      )}
      {visibleActions.includes('meeting') && (
        <ActionTrigger
          isLegacy={isLegacy}
          icon={faCalendarDays}
          title={actionNameMap.meeting}
          onClick={handleClick(() => {
            trackAction('meeting')
            setAction(
              <MeetingAction {...targetProps} orderContext={contextOrder} />,
              false,
              adaptablePosition
            )
          })}
          active={currentAction === 'meeting' && originalActionList}
          onActionHover={(hover) =>
            onActionHover?.(hover ? actionNameMap.meeting : null)
          }
          limitReached={
            meetingLimit !== undefined && targetIds.length > meetingLimit
              ? {
                  limit: meetingLimit,
                  label: 'rendez-vous'
                }
              : false
          }
        />
      )}
      {visibleActions.includes('addToOrder') && (
        <ActionTrigger
          isLegacy={isLegacy}
          icon={faLink}
          title={actionNameMap.addToOrder}
          onClick={handleClick(() => {
            trackAction('add_to_order')
            setAction(
              <AddToOrderAction applicantIds={targetIds} />,
              false,
              adaptablePosition
            )
          })}
          active={currentAction === 'addToOrder' && originalActionList}
          onActionHover={(hover) =>
            onActionHover?.(hover ? actionNameMap.addToOrder : null)
          }
          limitReached={
            applicantLinkOrderLimit !== undefined &&
            targetIds.length > applicantLinkOrderLimit
              ? {
                  limit: applicantLinkOrderLimit,
                  label: 'commande'
                }
              : false
          }
        />
      )}
      {!groupExtraActions &&
        extraActions.map((action) => (
          <ActionTrigger
            key={action.key}
            isLegacy={isLegacy}
            icon={action.icon}
            title={action.title}
            active={action.active}
            loading={action.loading}
            disabledReason={action.disabledReason}
            onClick={action.onClick}
            onActionHover={(hover) =>
              onActionHover?.(hover ? action.title : null)
            }
          />
        ))}
      {groupExtraActions && (
        <ActionsMenu
          align="end"
          trigger={(triggerProps, isMenuOpen) => (
            <span {...triggerProps}>
              <ActionTrigger
                isLegacy={isLegacy}
                icon={faEllipsisVertical}
                active={isMenuOpen}
              />
            </span>
          )}
        >
          {extraActions.map((action) => (
            <Tooltip key={action.key} text={action.disabledReason} asChild>
              <ActionsMenuItem
                icon={action.icon}
                title={action.title}
                loading={action.loading}
                disabled={action.loading || !!action.disabledReason}
                onClick={action.onClick}
              />
            </Tooltip>
          ))}
        </ActionsMenu>
      )}
    </div>
  )
}
